import { activeCssClass, makeNodeActive, removeCssClass } from "./utils.js";

const keyRegexp = /[\da-z]/;

/**
 * Register the keydown event on select for keyboard navigation.
 * This is solely made for internal use.
 */
export function registerButtonKeyDown({
	buttonRef,
	isOpen,
	onButtonClick,
	searchFieldRef,
	searchable,
	selectContainerRef,
	onOptionClick,
	nodeToItem,
}) {
	// eslint-disable-next-line sonarjs/cognitive-complexity -- don't want to change that now
	buttonRef.addEventListener("keydown", (event) => {
		switch (event.key) {
			case "Escape": {
				if (isOpen.current) {
					onButtonClick();
				}
				event.stopPropagation();
				return;
			}
			case "Enter": {
				if (!isOpen.current) {
					return;
				}

				const [activatedItem] =
					selectContainerRef.current.getElementsByClassName(
						activeCssClass,
					);
				if (!activatedItem) {
					return;
				}

				event.stopPropagation();
				event.preventDefault();

				onOptionClick(activatedItem);

				return;
			}
			case "ArrowUp": {
				if (isOpen.current) {
					activatePreviousOption({ selectContainerRef, nodeToItem });
				} else {
					onButtonClick();
					activateLastOption({ selectContainerRef, nodeToItem });
				}
				event.stopPropagation();
				return;
			}
			case "ArrowDown": {
				if (isOpen.current) {
					activateNextOption({ selectContainerRef, nodeToItem });
				} else {
					onButtonClick();
					activateFirstOption({ selectContainerRef, nodeToItem });
				}
				event.stopPropagation();
				return;
			}
			default: {
				const key = event.key;
				if (key.match(keyRegexp)) {
					event.stopPropagation();
					if (!isOpen.current) {
						onButtonClick();
					}
					if (searchable) {
						searchFieldRef.current?.childNodes[0].focus();
					} else {
						activateFirstItemStartingWith({
							key,
							selectContainerRef,
							nodeToItem,
						});
					}
				}
			}
		}
	});
}

function activateFirstOption({ selectContainerRef, nodeToItem }) {
	const firstNode = selectContainerRef.current.querySelector(
		"li.option:not([disabled])",
	);

	activateNode({ node: firstNode, nodeToItem });
}

function activateLastOption({ selectContainerRef, nodeToItem }) {
	const allNodes = selectContainerRef.current.querySelectorAll(
		"li.option:not([disabled])",
	);

	const lastNode = allNodes.at(-1);
	activateNode({ node: lastNode, nodeToItem });
}

function activatePreviousOption({ selectContainerRef, nodeToItem }) {
	const allNodes = [
		...selectContainerRef.current.querySelectorAll(
			"li.option:not([disabled])",
		),
	];

	const currentActiveNodeIndex = allNodes.findIndex((node) =>
		node.classList.contains(activeCssClass),
	);

	if (currentActiveNodeIndex === 0 || currentActiveNodeIndex === -1) {
		activateLastOption({ selectContainerRef, nodeToItem });
		return;
	}

	const previousNode = allNodes[currentActiveNodeIndex - 1];
	activateNode({ node: previousNode, nodeToItem });
}

function activateNextOption({ selectContainerRef, nodeToItem }) {
	const allNodes = [
		...selectContainerRef.current.querySelectorAll(
			"li.option:not([disabled])",
		),
	];

	const currentActiveNodeIndex = allNodes.findIndex((node) =>
		node.classList.contains(activeCssClass),
	);

	if (
		currentActiveNodeIndex === allNodes.length - 1 ||
		currentActiveNodeIndex === -1
	) {
		activateFirstOption({ selectContainerRef, nodeToItem });
		return;
	}

	const nextNode = allNodes[currentActiveNodeIndex + 1];
	activateNode({ node: nextNode, nodeToItem });
}

function activateNode({ node, nodeToItem }) {
	makeNodeActive({ node, nodeToItem });
	node.scrollIntoView();
}

function activateFirstItemStartingWith({
	key,
	selectContainerRef,
	nodeToItem,
}) {
	const allOptionNodes =
		selectContainerRef.current.getElementsByClassName("option");
	for (const li of allOptionNodes) {
		const item = nodeToItem[li];

		if (item?.label[0].toLowerCase() === key) {
			makeNodeActive({ node: li, nodeToItem });
			return;
		}
	}
	removeCssClass({ nodeToItem, cssClass: activeCssClass });
}
